import { renderMIPCreateSectionHeaderPortal } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/mipCreatePortals';
import { PersonalDataTagManagerModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/PersonalData/PersonalDataTagManagerModal/PersonalDataTagManagerModal';
import {
	TEST_ELEMENT_IDS,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import {
	CHIP_THEME,
	COLOR,
	Chips as ChipsV2,
} from '@dealstack/frontend/shared/components/Chips/Chips.v2';
import { Button } from '@dealstack/frontend/shared/components/fundament/Button/Button';
import { Checkbox } from '@dealstack/frontend/shared/components/fundament/Checkbox/Checkbox';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { SectionHeader } from '@dealstack/frontend/shared/components/SectionHeader/SectionHeader';
import { Tooltip } from '@dealstack/frontend/shared/components/Tooltip/Tooltip';
import { useTranslationMipCreate } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	INDIVIDUAL_DETAILS_DEFAULT_CHIPS,
	VEHICLE_DETAILS_DEFAULT_CHIPS,
} from '@dealstack/shared/lib/frontend/customDetails';
import { sortBy } from 'lodash';
import { memo } from 'react';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';

import { PREDEFINED_CUSTOM_DETAIL, usePersonalDataController } from './usePersonalDataController';
import { CustomDetailEnum } from '@dealstack/shared/lib/generated/models/CustomDetailEnum.ts';

interface Props {
	className?: string;
}

const PersonalData = ({ className }: Props) => {
	const tMipCreate = useTranslationMipCreate();
	const personalDataController = usePersonalDataController();
	const { individualArray, vehiclesArray } = personalDataController;

	return (
		<div className={className}>
			<FormProvider {...personalDataController.personalDetailsForm}>
				<form>
					{renderMIPCreateSectionHeaderPortal(
						<SectionHeader
							title={tMipCreate('personal_data.header.title')}
							subtitle={tMipCreate('personal_data.header.subtitle')}
						/>
					)}
					<SectionHeader
						size="h5b"
						title={tMipCreate('personal_data.individual_details.title')}
						subtitle={tMipCreate('personal_data.individual_details.subtitle')}
					/>
					<IndividualSection>
						<ChipWrapper>
							{INDIVIDUAL_DETAILS_DEFAULT_CHIPS.map((item) => (
								<PersonalDataChips
									key={item}
									prefixIcon="checkmark-circle-contained-filled"
									size="s"
									color={COLOR.GREEN}
									chipTheme={CHIP_THEME.LIGHT}
								>
									{item}
								</PersonalDataChips>
							))}
						</ChipWrapper>
						<QuestionText variant="p-lg">
							{tMipCreate('personal_data.individual_details.question')}
						</QuestionText>
						<UIElementIdentifier
							identifier={
								TEST_ELEMENT_IDS.MIP_CREATE_PERSONAL_DATA_INDIVIDUAL_INVESTORS_BANK_ACCOUNT_DETAILS_CHECKBOX
							}
						>
							<PersonalCheckbox
								label={tMipCreate('personal_data.shared.bank_account_details')}
								labelPlacement="right"
								checked={personalDataController.getCheckboxValue(
									PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_BANK
								)}
								onClick={() => {
									personalDataController.onCheckboxClicked(
										PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_BANK
									);
								}}
							/>
						</UIElementIdentifier>
						<UIElementIdentifier
							identifier={
								TEST_ELEMENT_IDS.MIP_CREATE_PERSONAL_DATA_INDIVIDUAL_INVESTORS_TAX_RESIDENCY_CHECKBOX
							}
						>
							<PersonalCheckbox
								label={tMipCreate('personal_data.individual_details.tax_residency')}
								labelPlacement="right"
								checked={personalDataController.getCheckboxValue(
									PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_TAX
								)}
								onClick={() => {
									personalDataController.onCheckboxClicked(
										PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_TAX
									);
								}}
								disabled={personalDataController.isTinChecked}
							/>
						</UIElementIdentifier>
						<UIElementIdentifier
							identifier={
								TEST_ELEMENT_IDS.MIP_CREATE_PERSONAL_DATA_INDIVIDUAL_INVESTORS_TIN_CHECKBOX
							}
						>
							<PersonalCheckbox
								label={tMipCreate('personal_data.individual_details.tax_id_number')}
								labelPlacement="right"
								checked={personalDataController.getCheckboxValue(
									PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_TIN
								)}
								onClick={() => {
									personalDataController.onCheckboxClicked(
										PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_TIN
									);
								}}
							/>
						</UIElementIdentifier>
						<UIElementIdentifier
							identifier={
								TEST_ELEMENT_IDS.MIP_CREATE_PERSONAL_DATA_INDIVIDUAL_INVESTORS_PLACE_BIRTH_CHECKBOX
							}
						>
							<PersonalCheckbox
								label={tMipCreate('personal_data.individual_details.place_birth')}
								labelPlacement="right"
								checked={personalDataController.getCheckboxValue(
									PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_PLACE_OF_BIRTH
								)}
								onClick={() => {
									personalDataController.onCheckboxClicked(
										PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_PLACE_OF_BIRTH
									);
								}}
							/>
						</UIElementIdentifier>
						<UIElementIdentifier
							identifier={
								TEST_ELEMENT_IDS.MIP_CREATE_PERSONAL_DATA_INDIVIDUAL_INVESTORS_COUNTRY_BIRTH_CHECKBOX
							}
						>
							<PersonalCheckbox
								label={tMipCreate(
									'personal_data.individual_details.country_birth',
									{
										defaultValue: 'Country of birth',
									}
								)}
								labelPlacement="right"
								checked={personalDataController.getCheckboxValue(
									PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_COUNTRY_OF_BIRTH
								)}
								onClick={() => {
									personalDataController.onCheckboxClicked(
										PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_COUNTRY_OF_BIRTH
									);
								}}
							/>
						</UIElementIdentifier>
						<UIElementIdentifier
							identifier={
								TEST_ELEMENT_IDS.MIP_CREATE_PERSONAL_DATA_INDIVIDUAL_INVESTORS_EMPLOYEE_ID_CHECKBOX
							}
						>
							<PersonalCheckbox
								label={tMipCreate('personal_data.individual_details.employee_id')}
								labelPlacement="right"
								checked={personalDataController.getCheckboxValue(
									PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_EMPLOYEE_ID
								)}
								onClick={() => {
									personalDataController.onCheckboxClicked(
										PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_EMPLOYEE_ID
									);
								}}
							/>
						</UIElementIdentifier>
						<UIElementIdentifier
							identifier={
								TEST_ELEMENT_IDS.MIP_CREATE_PERSONAL_DATA_INDIVIDUAL_INVESTORS_CITIZENSHIP_CHECKBOX
							}
						>
							<PersonalCheckbox
								label={tMipCreate('personal_data.individual_details.citizenship')}
								labelPlacement="right"
								checked={personalDataController.getCheckboxValue(
									PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_CITIZENSHIP
								)}
								onClick={() => {
									personalDataController.onCheckboxClicked(
										PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_CITIZENSHIP
									);
								}}
							/>
						</UIElementIdentifier>
						<CustomDetailsWrapper>
							{sortBy(individualArray ?? [], 'name')?.map((item, index) => {
								return (
									<UIElementIdentifier
										identifier={
											TEST_ELEMENT_IDS.MIP_CREATE_PERSONAL_DATA_DELETE_CUSTOM_INDIVIDUAL_INVESTOR_DATA_BUTTON
										}
										index={index}
									>
										<ChipsV2
											onClick={
												personalDataController.onOpenIndividualTagModal
											}
											key={item.id ?? item.localId}
											prefixIcon="checkmark-circle-contained-filled"
											suffixIcon={
												item.isCreatedFromPreFillableForm ||
												item.isCreatedFromGeneratableDocumentTemplate ? null : (
													<Icon
														variant="close"
														onClick={(event) => {
															event.stopPropagation();
															personalDataController.removeCustomDetailLocally(
																CustomDetailEnum.INDIVIDUAL,
																item.localId
															);
														}}
													/>
												)
											}
											size="s"
											color={COLOR.GREEN}
											chipTheme={CHIP_THEME.LIGHT}
										>
											{item.name}
										</ChipsV2>
									</UIElementIdentifier>
								);
							})}
						</CustomDetailsWrapper>
						<UIElementIdentifier
							identifier={
								TEST_ELEMENT_IDS.MIP_CREATE_PERSONAL_DATA_ADD_CUSTOM_INDIVIDUAL_INVESTOR_DATA_BUTTON
							}
						>
							<AddNewDetailButton
								onClick={personalDataController.onOpenIndividualTagModal}
								iconSize="lg"
								variant="text"
								icon="add-circle"
								iconPlacement="left"
							>
								{tMipCreate('personal_data.individual_details.add_button')}
							</AddNewDetailButton>
						</UIElementIdentifier>
					</IndividualSection>
					<SectionHeader
						size="h5b"
						title={tMipCreate('personal_data.vehicle_details.title')}
						subtitle={tMipCreate('personal_data.vehicle_details.subtitle')}
					/>
					<VehicleSection>
						<ChipWrapper>
							{VEHICLE_DETAILS_DEFAULT_CHIPS.map((item) => (
								<PersonalDataChips
									key={item}
									prefixIcon="checkmark-circle-contained-filled"
									suffixIcon={
										item === 'Legal representative' ? (
											<Tooltip
												tooltip={tMipCreate(
													'personal_data.vehicle_details.legal_representatives_tooltip'
												)}
											/>
										) : null
									}
									size="s"
									color={COLOR.GREEN}
									chipTheme={CHIP_THEME.LIGHT}
								>
									{item}
								</PersonalDataChips>
							))}
						</ChipWrapper>
						<QuestionText variant="p-lg">
							{tMipCreate('personal_data.vehicle_details.question')}
						</QuestionText>
						<UIElementIdentifier
							identifier={
								TEST_ELEMENT_IDS.MIP_CREATE_PERSONAL_DATA_PIV_BANK_ACCOUNT_DETAILS_CHECKBOX
							}
						>
							<PersonalCheckbox
								label={tMipCreate('personal_data.shared.bank_account_details')}
								labelPlacement="right"
								checked={personalDataController.getCheckboxValue(
									PREDEFINED_CUSTOM_DETAIL.VEHICLE_BANK
								)}
								onClick={() => {
									personalDataController.onCheckboxClicked(
										PREDEFINED_CUSTOM_DETAIL.VEHICLE_BANK
									);
								}}
							/>
						</UIElementIdentifier>
						<UIElementIdentifier
							identifier={
								TEST_ELEMENT_IDS.MIP_CREATE_PERSONAL_DATA_PIV_CONTROLLING_PARTY_CHECKBOX
							}
						>
							<PersonalCheckbox
								label={tMipCreate(
									'personal_data.vehicle_details.controlling_party_info'
								)}
								labelPlacement="right"
								checked={personalDataController.getCheckboxValue(
									PREDEFINED_CUSTOM_DETAIL.VEHICLE_CONTROLLING_PARTY
								)}
								onClick={() => {
									personalDataController.onCheckboxClicked(
										PREDEFINED_CUSTOM_DETAIL.VEHICLE_CONTROLLING_PARTY
									);
								}}
							/>
						</UIElementIdentifier>

						<CustomDetailsWrapper>
							{sortBy(vehiclesArray ?? [], 'name')?.map((item, index) => {
								return (
									<UIElementIdentifier
										identifier={
											TEST_ELEMENT_IDS.MIP_CREATE_PERSONAL_DATA_DELETE_CUSTOM_PIV_DATA_BUTTON
										}
										index={index}
									>
										<ChipsV2
											onClick={personalDataController.onOpenVehicleTagModal}
											key={item.id ?? item.localId}
											prefixIcon="checkmark-circle-contained-filled"
											suffixIcon={
												item.isCreatedFromPreFillableForm ? null : (
													<Icon
														variant="close"
														onClick={(event) => {
															event.stopPropagation();
															personalDataController.removeCustomDetailLocally(
																CustomDetailEnum.VEHICLE,
																item.localId
															);
														}}
													/>
												)
											}
											size="s"
											color={COLOR.GREEN}
											chipTheme={CHIP_THEME.LIGHT}
										>
											{item.name}
										</ChipsV2>
									</UIElementIdentifier>
								);
							})}
						</CustomDetailsWrapper>
						<UIElementIdentifier
							identifier={
								TEST_ELEMENT_IDS.MIP_CREATE_PERSONAL_DATA_ADD_CUSTOM_PIV_DATA_BUTTON
							}
						>
							<AddNewDetailButton
								onClick={personalDataController.onOpenVehicleTagModal}
								iconSize="lg"
								variant="text"
								icon="add-circle"
								iconPlacement="left"
							>
								{tMipCreate('personal_data.vehicle_details.add_button')}
							</AddNewDetailButton>
						</UIElementIdentifier>
					</VehicleSection>
					{personalDataController.isPersonalModalVisible ||
					personalDataController.isVehicleModalVisible ? (
						<PersonalDataTagManagerModal
							onClose={personalDataController.onTagManagerModalClose}
							data={
								personalDataController.isPersonalModalVisible
									? individualArray
									: vehiclesArray
							}
							formController={personalDataController.personalDetailsForm}
							detailsType={
								personalDataController.isPersonalModalVisible
									? CustomDetailEnum.INDIVIDUAL
									: CustomDetailEnum.VEHICLE
							}
							updateCustomDetails={personalDataController.updateCustomDetailsLocally}
						/>
					) : null}
				</form>
			</FormProvider>
		</div>
	);
};

const PersonalCheckbox = styled(Checkbox)`
	width: 15rem;
`;
const PersonalDataChips = styled(ChipsV2)`
	${Tooltip} {
		margin-left: 0em;
	}
`;

const QuestionText = styled(Text)`
	color: ${({ theme }) => theme.v2.color.neutral[700]};
`;

const AddNewDetailButton = styled(Button)``;
const ChipWrapper = styled.div`
	display: flex;
	${ChipsV2}:not(:last-of-type) {
		margin-right: 0.5em;
	}
`;
const IndividualSection = styled.div``;
const VehicleSection = styled.div``;
const CustomDetailsWrapper = styled.div`
	display: flex;
	gap: 0.5rem;
	flex-direction: row;
	flex-wrap: wrap;
`;
const Memoized = memo(PersonalData);
const Styled = styled(Memoized)`
	${SectionHeader} {
		margin-bottom: 1em;
	}

	${IndividualSection} {
		margin-bottom: 1em;
	}

	${QuestionText} {
		margin-bottom: 0.5em;
	}

	${ChipWrapper} {
		margin-bottom: 1.5em;
	}

	${AddNewDetailButton} {
		margin-top: 0.5em;
	}
	${CustomDetailsWrapper} ${ChipsV2} {
		margin-top: 0.5em;
	}
	${CustomDetailsWrapper} ${ChipsV2}:not(:last-of-type) {
		margin-right: 0.5em;
	}
`;

export { Styled as PersonalData };
